import React, { useEffect, useMemo, useState } from 'react';
import { useOverlay } from '../../contexts/OverlayContext';
import profileImageCache from '../../services/profileImageCache';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { Card, IconButton, PrimaryButton, ProfileImage, SearchBar } from '../ui';
import { employeeApi } from './employeeApi';

// Memoized ProfileImage wrapper to prevent re-renders
const MemoizedProfileImage = React.memo(ProfileImage, (prevProps, nextProps) => {
  return (
    prevProps.userId === nextProps.userId &&
    prevProps.imagePath === nextProps.imagePath &&
    prevProps.userName === nextProps.userName
  );
});
MemoizedProfileImage.displayName = 'MemoizedProfileImage';

// Memoized EmployeeCard component to prevent image flashing
const EmployeeCard = React.memo(({ employee, isSelected, isDisabled, employeeStatus, onToggle }) => {
  // Memoize ProfileImage to prevent remounting when selection changes
  const profileImage = useMemo(() => (
    <MemoizedProfileImage
      key={`profile-${employee.user_id}`}
      userId={employee.user_id}
      userName={employee.display_name}
      imagePath={employee.image_path}
      size="md"
      showLoading={false}
    />
  ), [employee.user_id, employee.display_name, employee.image_path]);

  return (
    <div>
      {isSelected ? (
        <Card.Gradient 
          className={`p-3 cursor-pointer transition-all duration-200 hover:shadow-lg ${isDisabled ? 'opacity-60' : ''}`}
          onClick={() => !isDisabled && onToggle(employee)}
        >
          <div className="flex justify-between items-center">
            <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
              {profileImage}
              <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                <div className="w-full text-black text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {employee.display_name}
                </div>
                <div className="w-full text-black text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {employee.role} • ID: {employee.employee_id}
                </div>
                {/* Status Message */}
                <div className={`text-xs font-medium ${
                  employeeStatus.status === 'assigned_to_zone' ? 'text-purple-600' :
                  employeeStatus.status === 'already_assigned' ? 'text-blue-600' :
                  employeeStatus.status === 'on_leave' ? 'text-orange-600' :
                  employeeStatus.status === 'assigned_to_shift' ? 'text-red-600' :
                  employeeStatus.status === 'local_conflict' ? 'text-red-500' :
                  'text-green-600'
                }`}>
                  {employeeStatus.statusMessage}
                </div>
              </div>
            </div>
            <div 
              data-property-1="check" 
              className="w-6 h-6 relative flex-shrink-0 ml-3"
            >
              <div className="w-6 h-6 left-0 top-0 absolute bg-green-700 rounded-sm flex items-center justify-center">
                <svg 
                  className="w-4 h-4 text-white"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </Card.Gradient>
      ) : (
        <Card.Dark 
          className={`p-3 cursor-pointer transition-all duration-200 hover:shadow-lg ${isDisabled ? 'opacity-60' : ''}`}
          onClick={() => !isDisabled && onToggle(employee)}
        >
          <div className="flex justify-between items-center">
            <div className="flex justify-start items-center gap-3 flex-1 min-w-0">
              {profileImage}
              <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                <div className="w-full text-white text-lg font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {employee.display_name}
                </div>
                <div className="w-full text-gray-300 text-sm font-normal font-['Open_Sans'] leading-relaxed truncate">
                  {employee.role} • ID: {employee.employee_id}
                </div>
                {/* Status Message */}
                <div className={`text-xs font-medium ${
                  employeeStatus.status === 'assigned_to_zone' ? 'text-purple-400' :
                  employeeStatus.status === 'already_assigned' ? 'text-blue-400' :
                  employeeStatus.status === 'on_leave' ? 'text-orange-400' :
                  employeeStatus.status === 'assigned_to_shift' ? 'text-red-400' :
                  employeeStatus.status === 'local_conflict' ? 'text-red-300' :
                  'text-green-400'
                }`}>
                  {employeeStatus.statusMessage}
                </div>
              </div>
            </div>
            <div 
              data-property-1="Default" 
              className="w-6 h-6 relative flex-shrink-0 ml-3"
            >
              <div className={`w-6 h-6 left-0 top-0 absolute rounded-sm ${
                isDisabled 
                  ? 'bg-gray-400 border-[1.60px] border-gray-500' 
                  : 'bg-zinc-100 border-[1.60px] border-black/30'
              }`} />
            </div>
          </div>
        </Card.Dark>
      )}
    </div>
  );
});

const AssignEmployees = ({ isOpen, onClose, onSave, initialSelectedEmployees = [], employeeType = 'guards', shiftValidationData = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState(initialSelectedEmployees);
  const [allEmployees, setAllEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employeeStatuses, setEmployeeStatuses] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [initialSelectedEmployeeIds, setInitialSelectedEmployeeIds] = useState(new Set());
  
  // Get context data and functions
  const { assignedEmployees, setAssignedEmployees } = useOverlay();

  // Utility function to check for time conflicts between shifts
  const checkTimeConflict = (startTime1, endTime1, startTime2, endTime2) => {
    // Convert time strings to minutes for easier comparison
    const timeToMinutes = (timeStr) => {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let totalMinutes = hours * 60 + minutes;
      if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
      if (period === 'AM' && hours === 12) totalMinutes -= 12 * 60;
      return totalMinutes;
    };

    const start1 = timeToMinutes(startTime1);
    const end1 = timeToMinutes(endTime1);
    const start2 = timeToMinutes(startTime2);
    const end2 = timeToMinutes(endTime2);

    // Check for overlap: one shift starts before the other ends
    return (start1 < end2 && start2 < end1);
  };

  // Load all employees when component opens
  useEffect(() => {
    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen, employeeType]);

  // Validate employee availability when employees are loaded and shift data is available
  useEffect(() => {
    if (isOpen && allEmployees.length > 0 && shiftValidationData && shiftValidationData.date && initialSelectedEmployeeIds.size >= 0) {
      validateEmployeeAvailability();
    }
  }, [isOpen, allEmployees, shiftValidationData, initialSelectedEmployeeIds]);

  // Fetch employees from API
  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      let employeesData = [];
      
      if (employeeType === 'guards') {
        employeesData = await employeeApi.fetchAvailableGuards();
      } else if (employeeType === 'supervisors') {
        employeesData = await employeeApi.fetchAvailableSupervisors();
      } else {
        // Fallback to all users
        employeesData = await employeeApi.getAllUsers();
      }

      // Process employees data to include profile photos
      const processedEmployees = await Promise.all(
        employeesData.map(async (employee) => {
          try {
            const imagePath = await employeeApi.getProfilePhoto(employee.user_id);
            
            // Cache the profile image if we have an image path
            // This ensures ProfileImage components can use cached URLs immediately
            if (imagePath && imagePath.trim() !== '') {
              const userId = employee.user_id || employee.employee_id;
              
              // Check if already cached
              const cachedUrl = profileImageCache.get(userId);
              if (!cachedUrl) {
                // If it's already a URL, cache it directly
                if (imagePath.startsWith('http')) {
                  profileImageCache.set(userId, imagePath);
                } else {
                  // It's an S3 path, cache the path and fetch presigned URL in background
                  profileImageCache.setS3Path(userId, imagePath);
                  // Fetch presigned URL in background (don't await to avoid blocking)
                  userService.getS3PresignedUrl(imagePath)
                    .then(presignedUrl => {
                      if (presignedUrl) {
                        profileImageCache.set(userId, presignedUrl, imagePath);
                        console.log(`💾 Cached profile image URL for user ${userId}`);
                      }
                    })
                    .catch(error => {
                      console.warn(`⚠️ Failed to cache presigned URL for user ${userId}:`, error);
                    });
                }
              }
            }
            
            return {
              ...employee,
              image_path: imagePath,
              display_name: employee.user_name || employee.name || 'Unknown',
              role: employee.spec_role || employee.role_name || employeeType,
              employee_id: employee.employee_id || employee.user_id
            };
          } catch (photoError) {
            console.warn(`Failed to fetch profile photo for ${employee.user_id}:`, photoError);
            return {
              ...employee,
              image_path: '',
              display_name: employee.user_name || employee.name || 'Unknown',
              role: employee.spec_role || employee.role_name || employeeType,
              employee_id: employee.employee_id || employee.user_id
            };
          }
        })
      );

      setAllEmployees(processedEmployees);
      setFilteredEmployees(processedEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError(error.message || 'Failed to fetch employees');
      setAllEmployees([]);
      setFilteredEmployees([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Validate employee availability for the current shift
  // This implements the complete Flutter validation logic from the provided guide
  const validateEmployeeAvailability = async () => {
    if (!shiftValidationData || !shiftValidationData.date) {
      console.log('No shift validation data available');
      return;
    }

    console.log('🔍 Starting employee validation with initial selected IDs:', Array.from(initialSelectedEmployeeIds));
    setIsValidating(true);
    const statuses = {};

    try {
      // Get current shift's zone assignments for conflict checking
      const currentShiftZoneGuards = shiftValidationData.currentShift?.zoneGuards || {};
      const currentShiftGuards = Object.values(currentShiftZoneGuards).flat();
      const currentShiftGuardIds = currentShiftGuards.map(guard => 
        typeof guard === 'string' ? guard : (guard.user_id || guard.employee_id)
      );

      // Validate each employee's availability
      const validationPromises = allEmployees.map(async (employee) => {
        const userId = employee.user_id || employee.employee_id;
        if (!userId) return;

        try {
          // Step 1: Check if employee is already assigned to this shift
          // This should only check against the initial selected employees, not the current selection
          const isAlreadyAssignedToThisShift = initialSelectedEmployeeIds.has(userId);
          console.log(`🔍 Employee ${employee.display_name} (${userId}): isAlreadyAssignedToThisShift = ${isAlreadyAssignedToThisShift}`);

          // Step 2: Check if employee is assigned to another zone in current shift
          const isAlreadyAssignedToZone = currentShiftGuardIds.includes(userId);

          // Step 3: Check if employee is assigned to another shift
          const isAssignedToShift = await employeeApi.isUserAssignedToShift({
            userId: userId,
            date: shiftValidationData.date,
            startTime: shiftValidationData.startTime,
            endTime: shiftValidationData.endTime
          });

          // Step 4: Check if employee is on leave
          let leaveAvailability;
          let isOnLeave = false;
          try {
            leaveAvailability = await employeeApi.checkLeaveAvailability({
              userId: userId,
              date: shiftValidationData.date
            });
            isOnLeave = !leaveAvailability.available;
          } catch (error) {
            console.error(`Failed to check leave status for employee ${userId} after retries:`, error);
            // Default to not on leave if API fails after retries
            leaveAvailability = {
              available: true,
              leave_type: null,
              leave_reason: null,
              start_date: null,
              end_date: null
            };
            isOnLeave = false;
          }

          // Step 5: Check for local time conflicts using isUserAssignedToShift
          let hasLocalConflict = false;
          try {
            // Check if user is already assigned to this specific time slot
            hasLocalConflict = await employeeApi.isUserAssignedToShift({
              userId: userId,
              date: shiftValidationData.date,
              startTime: shiftValidationData.startTime,
              endTime: shiftValidationData.endTime
            });
          } catch (error) {
            console.warn(`Could not check local conflicts for employee ${userId}:`, error);
            // Default to no conflict on error
            hasLocalConflict = false;
          }

          // Step 6: Determine status following the exact priority from the guide
          let status = 'available';
          let statusMessage = 'Available';
          let isAvailable = true;

          if (isAlreadyAssignedToZone) {
            status = 'assigned_to_zone';
            statusMessage = 'Assigned to another zone';
            isAvailable = false;
          } else if (isAlreadyAssignedToThisShift) {
            status = 'already_assigned';
            statusMessage = 'Already assigned to this shift';
            isAvailable = false;
          } else if (isOnLeave) {
            status = 'on_leave';
            statusMessage = 'On leave';
            isAvailable = false;
          } else if (isAssignedToShift) {
            status = 'assigned_to_shift';
            statusMessage = 'Assigned to another shift';
            isAvailable = false;
          } else if (hasLocalConflict) {
            status = 'local_conflict';
            statusMessage = 'Conflict with another shift';
            isAvailable = false;
          }

          console.log(`🔍 Final status for ${employee.display_name}: ${status} - ${statusMessage}`);

          statuses[userId] = {
            status,
            statusMessage,
            isAvailable,
            leaveDetails: leaveAvailability,
            isAlreadyAssignedToThisShift,
            isAlreadyAssignedToZone,
            isAssignedToShift,
            isOnLeave,
            hasLocalConflict
          };
        } catch (error) {
          console.error(`Error validating employee ${userId}:`, error);
          // Default to available on error
          statuses[userId] = {
            status: 'available',
            statusMessage: 'Available',
            isAvailable: true,
            leaveDetails: null,
            isAlreadyAssignedToThisShift: false,
            isAlreadyAssignedToZone: false,
            isAssignedToShift: false,
            isOnLeave: false,
            hasLocalConflict: false
          };
        }
      });

      await Promise.all(validationPromises);
      setEmployeeStatuses(statuses);
    } catch (error) {
      console.error('Error validating employee availability:', error);
    } finally {
      setIsValidating(false);
    }
  };

  // Update selected employees when initialSelectedEmployees prop changes
  useEffect(() => {
    setSelectedEmployees(initialSelectedEmployees);
    // Store the initial employee IDs for comparison
    const initialIds = new Set(initialSelectedEmployees.map(emp => 
      typeof emp === 'string' ? emp : (emp.user_id || emp.employee_id)
    ));
    setInitialSelectedEmployeeIds(initialIds);
  }, [initialSelectedEmployees]);

  // Trigger validation after initial selected employees are set
  useEffect(() => {
    if (isOpen && allEmployees.length > 0 && shiftValidationData && shiftValidationData.date && initialSelectedEmployeeIds.size >= 0) {
      // Add a small delay to ensure state is properly updated
      const timeoutId = setTimeout(() => {
        validateEmployeeAvailability();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [initialSelectedEmployeeIds]);

  // Reset selected employees when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedEmployees(initialSelectedEmployees);
    }
  }, [isOpen, initialSelectedEmployees]);

  // Handle search functionality
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length > 0) {
      // Filter employees based on search term
      const filtered = allEmployees.filter(employee => {
        const searchTerm = value.toLowerCase();
        return (
          employee.display_name?.toLowerCase().includes(searchTerm) ||
          employee.role?.toLowerCase().includes(searchTerm) ||
          employee.employee_id?.toLowerCase().includes(searchTerm) ||
          employee.user_id?.toLowerCase().includes(searchTerm)
        );
      });
      setFilteredEmployees(filtered);
      setSearchSuggestions(filtered.map(emp => emp.display_name));
    } else {
      // Show all employees when search is empty
      setFilteredEmployees(allEmployees);
      setSearchSuggestions([]);
    }
  };

  const handleSearchSubmit = (value) => {
    console.log('Search submitted:', value);
    // Add your search logic here
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    // Find the employee by display name and filter to show only that employee
    const selectedEmployee = allEmployees.find(emp => emp.display_name === suggestion);
    if (selectedEmployee) {
      setFilteredEmployees([selectedEmployee]);
    }
  };

  const handleAddEmployee = (employee) => {
    if (!selectedEmployees.find(emp => emp.user_id === employee.user_id)) {
      setSelectedEmployees([...selectedEmployees, employee]);
    }
    setSearchTerm('');
    setSearchSuggestions([]);
    // Reset to show all employees after adding
    setFilteredEmployees(allEmployees);
  };

  const handleRemoveEmployee = (employeeToRemove) => {
    setSelectedEmployees(selectedEmployees.filter(emp => emp.user_id !== employeeToRemove.user_id));
  };

  const handleToggleEmployee = (employee) => {
    const isSelected = selectedEmployees.find(emp => emp.user_id === employee.user_id);
    if (isSelected) {
      handleRemoveEmployee(employee);
    } else {
      handleAddEmployee(employee);
    }
  };

  const handleCloseDialog = () => {
    onClose();
    setSearchTerm('');
    setSearchSuggestions([]);
    setFilteredEmployees(allEmployees);
  };

  const handleSaveEmployees = () => {
    console.log('🔍 AssignEmployees - Saving employees:', selectedEmployees);
    setAssignedEmployees(selectedEmployees);
    onSave(selectedEmployees);
    setSearchTerm('');
    setSearchSuggestions([]);
  };

  return (
    <>
      {/* Dialog Box */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div className="w-full max-w-[464px] h-auto max-h-[90vh] sm:h-[682px] relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden flex flex-col my-auto">
            <div className="px-6 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4 flex justify-between items-start flex-shrink-0">
              <div className="flex flex-col flex-1 min-w-0 pr-2">
                <h3 className={typography.h3} style={{color: '#000000'}}>
                  Assign employees
                </h3>
                {selectedEmployees.length > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
              {/* Close Button */}
              <IconButton 
                onClick={handleCloseDialog}
                className="flex-shrink-0"
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
              />
            </div>

            {/* Dialog Content */}
            <div className="px-4 sm:px-6 md:px-8 pb-20 sm:pb-6 flex-1 flex flex-col min-h-0 overflow-hidden">
              {/* Search Bar in Dialog */}
              <div className="mb-6">
                <SearchBar
                  placeholder="Search for employees..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onSearch={handleSearchSubmit}
                  suggestions={searchSuggestions}
                  onSuggestionClick={handleSuggestionClick}
                  className="w-full" 
                />
              </div>

              {/* Employee List */}
              <div className="space-y-3 flex-1 overflow-y-auto pr-2 min-h-0">
                {isLoading ? (
                  <div className="text-center text-gray-500 py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
                    Loading employees...
                  </div>
                ) : isValidating ? (
                  <div className="text-center text-gray-500 py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    Checking employee availability...
                  </div>
                ) : error ? (
                  <div className="text-center text-red-500 py-8">
                    <div className="mb-2">⚠️</div>
                    <div className="text-sm">{error}</div>
                    <button 
                      onClick={fetchEmployees}
                      className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Retry
                    </button>
                  </div>
                ) : filteredEmployees.length > 0 ? (
                  filteredEmployees.map((employee, index) => {
                    const isSelected = selectedEmployees.find(emp => emp.user_id === employee.user_id);
                    const userId = employee.user_id || employee.employee_id;
                    const employeeStatus = employeeStatuses[userId] || { status: 'available', statusMessage: 'Available', isAvailable: true };
                    const isDisabled = !employeeStatus.isAvailable;
                    
                    return (
                      <EmployeeCard
                        key={userId || `employee-${index}`}
                        employee={employee}
                        isSelected={isSelected}
                        isDisabled={isDisabled}
                        employeeStatus={employeeStatus}
                        onToggle={handleToggleEmployee}
                      />
                    );
                  })
                ) : searchTerm.length > 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    No employees found matching "{searchTerm}"
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    No employees available
                  </div>
                )}
              </div>

              {/* Dialog Actions */}
              <div className="mt-4 sm:mt-6 pt-4 border-t border-gray-200 flex gap-3 justify-center flex-shrink-0">
                <PrimaryButton 
                  onClick={handleSaveEmployees}
                  disabled={selectedEmployees.length === 0}
                  className="w-full sm:w-auto"
                >
                  Confirm {selectedEmployees.length > 0 && `(${selectedEmployees.length})`}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssignEmployees;
